import { computed } from 'vue'
import type { Ref } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { useRpQuery } from '~/composables/graphql'
import { graphql } from '~/resources/graphql'
import { useActiveFacility } from '~/composables/use-active-facility'
import { useCurrentDate } from '~/composables/use-current-date'
import type { Language } from '~/resources/graphql/graphql'
import { useSelectedLanguage } from '~/composables/use-selected-language'

export type PurchaseAgreementInput = {
    language: Language | null
    planId: string
}

export function usePurchaseAgreement(
    purchaseAgreementInput: Ref<PurchaseAgreementInput | null | undefined>
) {
    const query = useRpQuery()
    const activeFacility = useActiveFacility()
    const activeFacilityId = computed(() => activeFacility.value.id)
    const currentDate = useCurrentDate()
    const reactiveDate = computed(() => currentDate.value.getDate())
    const selectedLanguage = useSelectedLanguage()
    const effectiveLanguage = computed<Language>(() => {
        // Null plan language means the agreement should follow the customer's selected storefront language.
        return purchaseAgreementInput.value?.language ?? selectedLanguage.value
    })

    const { data, suspense } = useQuery({
        queryKey: [
            'PurchaseAgreementQuery',
            purchaseAgreementInput,
            activeFacilityId,
            reactiveDate,
            effectiveLanguage
        ],
        queryFn: () => {
            if (
                purchaseAgreementInput.value === undefined ||
                purchaseAgreementInput.value === null
            ) {
                return null
            }

            return query(
                graphql(/* GraphQL */ `
                    query PurchaseAgreementQuery(
                        $planId: ID!
                        $facilityId: ID!
                        $input: SimpleSpokeInput!
                        $language: Language!
                    ) {
                        plan(id: $planId) {
                            ...PurchaseAgreementPlanFragment
                        }
                    }
                `),
                {
                    planId: purchaseAgreementInput.value.planId,
                    facilityId: activeFacilityId.value,
                    input: {
                        facilityId: activeFacilityId.value
                    },
                    language: effectiveLanguage.value
                }
            )
        }
    })

    const plan = computed(() => {
        if (purchaseAgreementInput.value === undefined) {
            return undefined
        }

        if (purchaseAgreementInput.value === null) {
            return null
        }

        if (data.value === undefined) {
            return undefined
        }

        if (data.value === null) {
            return null
        }

        return data.value.plan
    })

    return {
        plan,
        suspense
    }
}
